import {
  fetchLoginHistory,
  type LoginHistoryPage as HistoryPage,
  type LoginEvent,
  type LoginEventKind,
} from "../api.ts";
import { CheckCircleIcon, CrossCircleIcon, LaptopIcon, PhoneIcon } from "../components/icons.tsx";
import { AppLink } from "../components/Layout.tsx";
import { Card, LoadState, Note, PageHeader } from "../components/ui.tsx";
import { formatDateTime } from "../format.ts";
import { ROUTES } from "../router.ts";
import { useLoader } from "../use-loader.ts";

const KIND_LABELS: Record<LoginEventKind, string> = {
  login_succeeded: "ログイン",
  login_failed: "ログインの失敗",
  logged_out: "ログアウト",
  mfa_succeeded: "多要素認証",
  mfa_failed: "多要素認証の失敗",
  mfa_enrolled: "多要素認証の登録",
  password_changed: "パスワードの変更",
  password_change_requested: "パスワード変更メールの送信",
};

const FAILURE_KINDS = new Set<LoginEventKind>(["login_failed", "mfa_failed"]);

/** 新しい順の最初のページ（Auth0 のログ 100 件分）だけを出す。保持期間（10 日）より古い記録は Auth0 に無い */
const loadLatest = () => fetchLoginHistory(0);

export function LoginHistoryPage() {
  const { state, reload } = useLoader<HistoryPage>(loadLatest);
  const retentionDays = state.status === "loaded" ? state.data.retentionDays : 10;

  return (
    <>
      <PageHeader
        title="ログイン記録"
        description={`このアカウントの直近 ${retentionDays} 日間のログインに関する記録です（新しいものから最大 100 件）。`}
      />
      <Note tone="caution">
        心当たりのない記録がある場合は、すぐにパスワードを変更してください。
        <AppLink to={ROUTES.security} className="note__link">
          パスワードを変更する
        </AppLink>
      </Note>
      <Card labelledBy="history-heading">
        <h2 id="history-heading" className="card__title">
          記録
        </h2>
        {state.status === "loaded" ? (
          state.data.items.length === 0 ? (
            <p className="empty">直近 {retentionDays} 日間の記録はありません。</p>
          ) : (
            // 5 件ほどの高さで中をスクロールさせる
            <section className="event-scroll" aria-label="ログイン記録の一覧">
              <ul className="event-list">
                {state.data.items.map((event, i) => (
                  <EventItem key={event.id ?? `${event.date}-${i}`} event={event} />
                ))}
              </ul>
            </section>
          )
        ) : (
          <LoadState state={state.status} onRetry={reload} />
        )}
      </Card>
    </>
  );
}

function EventItem({ event }: { event: LoginEvent }) {
  const failed = FAILURE_KINDS.has(event.kind);
  const mobile = event.os === "iOS" || event.os === "Android";
  const device = [event.browser, event.os].filter(Boolean).join(" · ");
  const place = [placeText(event.location), event.ip].filter(Boolean).join(" · ");
  return (
    <li className="event">
      <span className={`event__icon${failed ? " event__icon--failed" : ""}`} aria-hidden="true">
        {mobile ? <PhoneIcon size={22} /> : <LaptopIcon size={22} />}
      </span>
      <div className="event__body">
        <div className="event__title">
          {failed ? <CrossCircleIcon size={16} /> : <CheckCircleIcon size={16} />}
          <span>{KIND_LABELS[event.kind]}</span>
          {failed ? <span className="badge badge--danger">要確認</span> : null}
        </div>
        <dl className="meta">
          <dt>日時</dt>
          <dd>{formatDateTime(event.date) ?? "—"}</dd>
          <dt>場所</dt>
          <dd>{place || "不明"}</dd>
          <dt>ブラウザ・OS</dt>
          <dd>{device || "不明"}</dd>
        </dl>
        {event.product !== null ? (
          <div className="chips">
            <span className="chip">{event.product}</span>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function placeText(location: LoginEvent["location"]): string | null {
  if (location === null) return null;
  const parts = [location.city, location.country].filter(Boolean);
  return parts.length === 0 ? null : `${parts.join("、")}（IP から推定）`;
}
